<template>
  <div
    class="issues-category-card"
    :class="{
      'has-right-side': showRightSide,
      'has-thresholds-expanded': showAlertThresholds,
      'is-row-clickable': routeProductId,
      'is-highlighted': isHighlighted
    }"
    :data-alert-id="alertDomId"
    :tabindex="routeProductId ? 0 : -1"
    @click="openProductDetails"
    @keydown.enter.prevent="openProductDetails"
    @keydown.space.prevent="openProductDetails"
  >
    <!-- Left: Status icon + Product thumbnail + Product name + Status badge -->
    <div class="issues-category-card-left">
      <div class="issues-category-card-icon" :class="statusClass">
        <i v-if="statusClass === 'resolved'" class="check icon"></i>
        <i v-else-if="statusClass === 'ok'" class="check icon"></i>
        <i v-else-if="statusClass === 'suppressed'" class="ban icon"></i>
        <i v-else-if="statusClass === 'alert'" class="ban icon"></i>
        <i v-else-if="statusClass === 'rotating'" class="sync alternate icon"></i>
        <i v-else-if="statusClass === 'lost'" class="time icon"></i>
        <i v-else class="time icon"></i>
      </div>
      <template v-if="productThumbUrl">
        <img :src="productThumbUrl" class="issues-category-card-thumb" alt="" loading="lazy" decoding="async" />
      </template>
      <div v-else class="issues-category-card-thumb-placeholder" :class="{ 'is-clickable': routeProductId }">
        <i class="image icon"></i>
      </div>
      <div class="issues-category-card-product">
        <span class="issues-category-card-title">
          <span
            class="issues-category-card-title-text"
            :class="{ 'issues-category-card-title-link': routeProductId }"
          >{{ productTitle }}</span>
        </span>
        <span v-if="displayAsin" class="issues-category-card-asin">{{ feeSku ? feeSku + ' · ' + displayAsin : displayAsin }}</span>
        <span :class="['issues-category-card-status', statusClass]">{{ statusLabel }}</span>
      </div>
    </div>

    <div class="issues-category-card-marketplace" @click.stop>
      <button
        v-if="marketplaceCode"
        type="button"
        class="issues-marketplace-chip"
        :class="{ 'is-filtered': marketplaceFilterActive }"
        :title="'Filter alerts to ' + marketplaceLabel"
        @click="onMarketplaceChipClick"
      >
        <i :class="marketplaceFlagClass"></i>
        <span>{{ marketplaceLabel }}</span>
      </button>
      <span v-else class="issues-marketplace-empty">—</span>
    </div>

    <!-- Right: Message + Actions + Date (only for actionable issues - e.g. LOST) -->
    <template v-if="showRightSide">
      <div class="issues-category-card-right">
        <template v-if="changeDescriptionTableRows">
          <div class="issues-change-desc-table-wrap">
            <table class="issues-change-desc-table" role="grid" aria-label="Listing changes">
              <thead>
                <tr>
                  <th scope="col">Field</th>
                  <th scope="col">Before</th>
                  <th scope="col">After</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in changeDescriptionTableRows" :key="idx">
                  <td class="issues-change-desc-field">{{ row.field }}</td>
                  <td
                    class="issues-change-desc-cell"
                    :title="row.beforeTitle ? row.beforeTitle : undefined"
                  >{{ row.beforeDisplay }}</td>
                  <td
                    class="issues-change-desc-cell"
                    :title="row.afterTitle ? row.afterTitle : undefined"
                  >{{ row.afterDisplay }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <div v-else-if="reviewHealthInfo" class="issues-category-card-message issues-review-health">
          <div v-for="row in reviewHealthInfo.rows" :key="row.reason" class="issues-review-health-row">{{ row.text }}</div>
          <div v-if="!reviewHealthInfo.rows.length && item.statusDetails && item.statusDetails.message" class="issues-review-health-row">{{ item.statusDetails.message }}</div>
          <div
            v-if="reviewHealthInfo.variationAsins.length"
            class="issues-review-health-variations"
          >Also affects {{ reviewHealthInfo.variationAsins.length }} variation{{ reviewHealthInfo.variationAsins.length === 1 ? '' : 's' }}: {{ reviewHealthInfo.variationAsins.join(', ') }}</div>
        </div>
        <div v-else class="issues-category-card-message">
          {{ item.statusDetails && item.statusDetails.message ? item.statusDetails.message : '' }}
          <div v-if="feeReason" class="issues-fee-reason" :class="'is-' + feeReason.tone">
            <span v-if="feeReason.lead" class="issues-fee-reason-lead">{{ feeReason.lead }}</span>{{ feeReason.rest }}
          </div>
        </div>
        <div class="issues-category-card-actions" @click.stop>
          <a v-if="sellerCentralUrl"
             :href="sellerCentralUrl"
             target="_blank"
             rel="noopener noreferrer"
             class="issues-category-card-btn issues-category-card-btn-orange"
             @click.stop>
            View in Seller Central
          </a>
          <a v-if="viewUrl"
             :href="viewUrl"
             target="_blank"
             rel="noopener noreferrer"
             class="issues-category-card-btn"
             :class="sellerCentralUrl ? 'issues-category-card-btn-blue' : 'issues-category-card-btn-orange'"
             @click.stop>
            {{ viewUrlLabel }}
          </a>
          <a v-if="feeClaimDownloadUrl"
             :href="feeClaimDownloadUrl"
             target="_blank"
             rel="noopener noreferrer"
             class="issues-category-card-btn issues-category-card-btn-green"
             @click.stop>
            Download orders
          </a>
          <button
            v-if="showMarkResolvedButton"
            type="button"
            class="issues-category-card-btn issues-category-card-btn-resolve"
            :disabled="markResolveBusy"
            @click.stop="$emit('mark-resolved', item)"
          >
            Mark as resolved
          </button>
          <button v-if="showAlertThresholdsButton"
                  class="issues-category-card-btn issues-category-card-btn-settings"
                  :class="{ active: showAlertThresholds }"
                  title="Alert thresholds"
                  @click.stop="toggleAlertThresholds">
            <i class="cog icon"></i>
          </button>
          <a v-if="item.muted" href="#" class="issues-category-card-link" @click.stop.prevent="mute(-1)">Un-mute</a>
        </div>
      </div>

      <div v-if="item.date" class="issues-category-card-date">
        {{ formatDate(item.date) }}
      </div>
    </template>

    <!-- Inline Alert Thresholds (expanded when settings clicked) -->
    <div v-if="showAlertThresholds && showAlertThresholdsButton" class="issues-category-card-thresholds" @click.stop>
      <AlertThresholdsInline
        v-if="wdtype === 'inventory'"
        :item="item"
        :product-id="routeProductId"
        :seller-account-id="sellerAccountIdForThresholds"
        :product-title="productTitle"
        :marketplace="item.marketplace || ''"
        :stock-thresholds-api-base="stockAlertThresholdsApiBaseUrl"
        :api-base-url="apiBaseUrl"
        @saved="onThresholdsSaved"
        @cancel="showAlertThresholds = false"
      />
      <fbm-alert-thresholds-inline
        v-else-if="wdtype === 'fbmInventory'"
        :item="item"
        :product-id="routeProductId"
        :seller-account-id="sellerAccountIdForThresholds"
        :product-title="productTitle"
        :stock-thresholds-api-base="stockAlertThresholdsApiBaseUrl"
        :api-base-url="apiBaseUrl"
        @saved="onThresholdsSaved"
        @cancel="showAlertThresholds = false"
      />
      <sales-volume-thresholds-inline
        v-else-if="wdtype === 'salesDrop'"
        :item="item"
        :product-id="routeProductId"
        :seller-account-id="sellerAccountIdForThresholds"
        :product-title="productTitle"
        :marketplace="item.marketplace || ''"
        :sales-volume-thresholds-api-base="salesVolumeThresholdsApiBaseUrl"
        @saved="onThresholdsSaved"
        @cancel="showAlertThresholds = false"
      />
    </div>
  </div>
</template>

<script>
import AlertThresholdsInline from './AlertThresholdsInline.vue'
import FBMAlertThresholdsInline from './FBMAlertThresholdsInline.vue'
import SalesVolumeThresholdsInline from './SalesVolumeThresholdsInline.vue'
import {
  parseProductDetailChangeRows,
  formatChangeCellBefore,
  formatChangeCellAfter,
  truncateChangeCell,
  isImageOnlyUpdatedRows
} from '../../utils/productDetailChangeMessage.js'
import { reviewHealthDetails } from '../../utils/reviewHealth.js'
import {
  amazonHostForMarketplace,
  amazonListingUrl,
  extractAsinFromUrl,
  marketplaceColumnLabel,
  marketplaceFlagClass,
  normalizeMarketplaceCode,
  sellerCentralAsinUrl
} from '../../utils/amazonMarketplaceUrls.js'

function isLikelyHttpImageUrl (url) {
  if (!url || typeof url !== 'string') return false
  var t = url.trim()
  return /^https?:\/\//i.test(t)
}

export default {
  name: 'IssuesCategoryCard',
  components: {
    AlertThresholdsInline,
    'fbm-alert-thresholds-inline': FBMAlertThresholdsInline,
    'sales-volume-thresholds-inline': SalesVolumeThresholdsInline
  },
  props: {
    item: { type: Object, required: true },
    wdtype: { type: String, required: true },
    products: { type: Object, default: () => ({}) },
    statusLabel: { type: String, default: '' },
    statusClass: { type: String, default: '' },
    apiBaseUrl: { type: String, default: '/api/v1/watchdogs' },
    stockAlertThresholdsApiBaseUrl: { type: String, default: '/api/v1/stock-alert-thresholds' },
    salesVolumeThresholdsApiBaseUrl: { type: String, default: '/api/v1/sales-volume-alert-thresholds' },
    sellerAccountIdForThresholds: { type: String, default: null },
    markResolveBusy: { type: Boolean, default: false },
    /** 'active' | 'resolved' — hide resolve affordances on resolved tab */
    issuesListTab: { type: String, default: 'active' },
    highlightedAlertId: { type: String, default: '' },
    filterByMarket: { type: Array, default: function () { return [] } }
  },
  data: function () {
    return {
      showAlertThresholds: false
    }
  },
  computed: {
    showRightSide: function () {
      if (this.statusClass === 'muted') return false
      /* Resolved rows: no status message/actions, but keep Amazon / Seller Central links when available */
      if (this.statusClass === 'resolved') return !!(this.viewUrl || this.sellerCentralUrl)
      if (this.statusClass === 'suppressed' || this.statusClass === 'rotating') return false
      return true
    },
    showMarkResolvedButton: function () {
      if (this.issuesListTab === 'resolved') return false
      if (!this.showRightSide || !this.item) return false
      if (!this.item.watchDogId || this.item.muted) return false
      return this.item.issueStatus !== 3
    },
    showAlertThresholdsButton: function () {
      if (!(this.wdtype === 'inventory' || this.wdtype === 'fbmInventory' || this.wdtype === 'salesDrop')) return false
      if (!this.item) return false
      if (this.wdtype === 'salesDrop') return !!(this.item.watchDogId && this.routeProductId)
      return !!(this.item.watchDogId || this.routeProductId)
    },
    marketplaceCode: function () {
      var item = this.item || {}
      var p = this.product || {}
      return normalizeMarketplaceCode(item.marketplace || p.marketplace)
    },
    marketplaceLabel: function () {
      return marketplaceColumnLabel(this.marketplaceCode)
    },
    marketplaceFlagClass: function () {
      return marketplaceFlagClass(this.marketplaceCode)
    },
    marketplaceFilterActive: function () {
      var code = this.marketplaceCode
      if (!code || !this.filterByMarket || this.filterByMarket.length === 0) return false
      for (var i = 0; i < this.filterByMarket.length; i++) {
        if (normalizeMarketplaceCode(this.filterByMarket[i]) === code) return true
      }
      return false
    },
    /** fbaFeeChange: the SKU whose fee moved (statusDetails.sellerSku on per-SKU alerts), else the product's. */
    feeSku: function () {
      if (this.wdtype !== 'fbaFeeChange') return ''
      var d = (this.item && this.item.statusDetails) || {}
      var sku = d.sellerSku || (this.product && this.product.sku) || ''
      return sku && String(sku).toUpperCase() !== this.displayAsin ? String(sku) : ''
    },
    /**
     * fbaFeeChange, fee claims tenants: statusDetails.reasonLine under the message. A re-measure leads with
     * "Amazon re-measured this product" in green (fee down) or amber (fee up); other reasons are one muted line.
     */
    feeReason: function () {
      if (this.wdtype !== 'fbaFeeChange') return null
      var d = (this.item && this.item.statusDetails) || {}
      var line = d.reasonLine ? String(d.reasonLine) : ''
      if (!line) return null
      var tone = d.reason === 'remeasured_down' ? 'down' : d.reason === 'remeasured_up' ? 'up' : 'info'
      var at = line.indexOf(' · ')
      if (tone === 'info' || at < 0) return { tone: tone, lead: tone === 'info' ? '' : line, rest: tone === 'info' ? line : '' }
      return { tone: tone, lead: line.slice(0, at) + ': ', rest: line.slice(at + 3) }
    },
    /** fbaFeeChange, re-measured decrease with orders to claim: the signed order list link. */
    feeClaimDownloadUrl: function () {
      if (this.wdtype !== 'fbaFeeChange' || this.issuesListTab === 'resolved') return null
      var claim = this.item && this.item.statusDetails && this.item.statusDetails.feeClaim
      return claim && claim.downloadUrl ? claim.downloadUrl : null
    },
    displayAsin: function () {
      var item = this.item || {}
      var p = this.product || {}
      var asin = item.asin || p.asin || extractAsinFromUrl(p.productUrl || '')
      return asin ? String(asin).toUpperCase() : ''
    },
    alertDomId: function () {
      var item = this.item || {}
      if (item.watchDogId != null && item.watchDogId !== '') return String(item.watchDogId)
      if (item.watchDogItemId != null && item.watchDogItemId !== '') return String(item.watchDogItemId)
      return ''
    },
    isHighlighted: function () {
      if (!this.highlightedAlertId || !this.item) return false
      var t = String(this.highlightedAlertId)
      if (this.item.watchDogId != null && String(this.item.watchDogId) === t) return true
      if (this.item.watchDogItemId != null && String(this.item.watchDogItemId) === t) return true
      return false
    },
    amazonListingUrl: function () {
      var p = this.product || {}
      var asin = this.displayAsin
      var fallback = p.productUrl || null
      if (!asin && !fallback) return null
      return amazonListingUrl(asin, this.marketplaceCode, fallback)
    },
    sellerCentralUrl: function () {
      if (this.wdtype === 'badReview' || this.wdtype === 'topReview') return null
      return sellerCentralAsinUrl(this.displayAsin, this.marketplaceCode)
    },
    /** reviewHealth only: before → after rows per reason, variations, reviews link (snake or camel keys). */
    reviewHealthInfo: function () {
      if (this.wdtype !== 'reviewHealth') return null
      return reviewHealthDetails(this.item && this.item.statusDetails)
    },
    viewUrl: function () {
      if (this.reviewHealthInfo && this.reviewHealthInfo.reviewsUrl) return this.reviewHealthInfo.reviewsUrl
      if (this.wdtype === 'badReview' && this.product && this.product.productReviewsUrl) {
        return this.rewriteAmazonUrlHost(this.product.productReviewsUrl) || this.product.productReviewsUrl
      }
      if (this.wdtype === 'topReview' && this.product && this.product.productTopReviewsUrl) {
        return this.rewriteAmazonUrlHost(this.product.productTopReviewsUrl) || this.product.productTopReviewsUrl
      }
      return this.amazonListingUrl
    },
    viewUrlLabel: function () {
      if (this.wdtype === 'badReview') return 'Latest Reviews'
      if (this.wdtype === 'topReview') return 'Top Rated Reviews'
      if (this.reviewHealthInfo && this.reviewHealthInfo.reviewsUrl) return 'Read reviews on Amazon'
      return 'View on Amazon'
    },
    product: function () {
      return this.item.productId && this.products[this.item.productId] ? this.products[this.item.productId] : null
    },
    productThumbUrl: function () {
      if (!this.product) return null
      var u = this.product.largeImageUrl || this.product.imageUrl
      return isLikelyHttpImageUrl(u) ? String(u).trim() : null
    },
    productTitle: function () {
      if (this.product) return this.product.productTitle || ''
      if (this.item.title && String(this.item.title).trim()) return String(this.item.title).trim()
      return '(Product data unavailable)'
    },
    routeProductId: function () {
      if (this.item.productId != null && this.item.productId !== '') return this.item.productId
      return null
    },
    changeDescriptionTableRows: function () {
      var tableWdtypes = {
        productDetailsRecentChange: true,
        productCategoryRecentChange: true,
        parentProductChange: true
      }
      if (!tableWdtypes[this.wdtype]) return null
      var msg = this.item && this.item.statusDetails && this.item.statusDetails.message
      if (!msg) return null
      var parsed = parseProductDetailChangeRows(String(msg))
      if (!parsed || isImageOnlyUpdatedRows(parsed)) return null
      return parsed.map(function (r) {
        var bRaw = formatChangeCellBefore(r.before)
        var aRaw = formatChangeCellAfter(r.after)
        var bd = truncateChangeCell(bRaw)
        var ad = truncateChangeCell(aRaw)
        return {
          field: r.field,
          beforeDisplay: bd.text,
          afterDisplay: ad.text,
          beforeTitle: bd.truncated ? bRaw : '',
          afterTitle: ad.truncated ? aRaw : ''
        }
      })
    }
  },
  methods: {
    rewriteAmazonUrlHost: function (url) {
      if (!url || typeof url !== 'string') return null
      try {
        var u = new URL(url)
        if (!/amazon\./i.test(u.hostname)) return null
        u.hostname = amazonHostForMarketplace(this.marketplaceCode, url)
        return u.toString()
      } catch (e) {
        return null
      }
    },
    onMarketplaceChipClick: function () {
      if (!this.marketplaceCode) return
      this.$emit('filter-marketplace', this.marketplaceCode)
    },
    formatDate: function (d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('en-US')
    },
    mute: function (minutes) {
      this.$emit('mute', this.item, minutes)
    },
    openProductDetails: function () {
      if (!this.routeProductId) return
      this.$emit('view-product', this.routeProductId)
    },
    toggleAlertThresholds: function () {
      this.showAlertThresholds = !this.showAlertThresholds
    },
    onThresholdsSaved: function () {
      this.showAlertThresholds = false
      this.$emit('saved')
    }
  }
}
</script>



// WEBPACK FOOTER //
// IssuesCategoryCard.vue?7e96fdc1